import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { CloudSun, Loader2, RefreshCw, ShieldCheck, AlertTriangle, ShieldAlert, OctagonAlert } from "lucide-react";

const IMPACT_STYLES = {
  low: {
    cls: "border-emerald-400/60 bg-emerald-900/30 text-emerald-100",
    Icon: ShieldCheck,
    label: "LOW IMPACT",
    labelCls: "bg-emerald-500/30 text-emerald-200",
  },
  medium: {
    cls: "border-amber-400/60 bg-amber-900/30 text-amber-100",
    Icon: AlertTriangle,
    label: "MEDIUM IMPACT",
    labelCls: "bg-amber-500/30 text-amber-200",
  },
  high: {
    cls: "border-orange-400/60 bg-orange-900/30 text-orange-100",
    Icon: ShieldAlert,
    label: "HIGH IMPACT",
    labelCls: "bg-orange-500/30 text-orange-200",
  },
  critical: {
    cls: "border-red-500/70 bg-red-900/40 text-red-100",
    Icon: OctagonAlert,
    label: "CRITICAL",
    labelCls: "bg-red-500/40 text-red-200",
  },
};

export default function StateWeather() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [unit, setUnit] = useState("C"); // C | F

  async function load(force = false) {
    setLoading(true);
    try {
      const r = await api.get("/dashboard/state-weather" + (force ? "?force=1" : ""));
      setRows(r.data?.rows || []);
      setGeneratedAt(r.data?.generated_at || null);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    const t = setInterval(load, 30 * 60 * 1000); // 30 minutes
    return () => clearInterval(t);
  }, []);

  const toUnit = (c) => unit === "F" ? Math.round(c * 9 / 5 + 32) : Math.round(c);
  const uSym = unit === "F" ? "°F" : "°C";

  return (
    <section className="" data-testid="state-weather-widget">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="industrial-label flex items-center gap-2 text-ncc-gold">
            <CloudSun className="h-4 w-4" /> Live Multi-State Weather
          </div>
          <p className="mt-1 text-xs text-blue-100/80">
            Real-time weather conditions across your top manpower-source states.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-white/30">
            <button type="button" onClick={() => setUnit("C")} data-testid="weather-unit-c"
              className={`px-3 py-1 text-xs font-bold ${unit === "C" ? "bg-ncc-gold text-ncc-navy" : "bg-transparent text-white"}`}>
              °C
            </button>
            <button type="button" onClick={() => setUnit("F")} data-testid="weather-unit-f"
              className={`px-3 py-1 text-xs font-bold ${unit === "F" ? "bg-ncc-gold text-ncc-navy" : "bg-transparent text-white"}`}>
              °F
            </button>
          </div>
          <button type="button" onClick={() => load(true)} data-testid="weather-refresh"
            className="inline-flex items-center gap-2 border border-white/30 bg-transparent px-3 py-1 text-xs font-bold text-white transition-colors hover:bg-white/10">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-3 grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
        {loading && rows.length === 0
          ? Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-44 animate-pulse rounded-md border border-white/10 bg-white/5" />
            ))
          : rows.map((r) => {
              const impact = r.mobilisation_impact || { level: "low", title: "—", detail: "—", pct: 0 };
              const style = IMPACT_STYLES[impact.level] || IMPACT_STYLES.low;
              const ToneIcon = style.Icon;
              return (
                <div key={r.state} className="overflow-hidden rounded-md border border-white/15 bg-[#0a1f4a]/70 backdrop-blur-sm" data-testid={`weather-card-${r.state}`}>
                  <div className="flex items-center justify-between px-3 pt-3">
                    <div>
                      <div className="font-heading text-sm font-extrabold uppercase tracking-wider text-white">{r.state}</div>
                      <div className="font-mono text-[10px] text-blue-100/70">{r.capital}</div>
                    </div>
                    <span className="flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 font-mono text-[9px] font-bold text-emerald-300">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Live
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-3 py-3">
                    <div className="text-4xl" aria-hidden="true">{r.icon || "☀"}</div>
                    <div className="text-right">
                      <div className="font-heading text-3xl font-black text-white leading-none">
                        {r.temp_c != null ? toUnit(r.temp_c) : "—"}<span className="text-base">{uSym}</span>
                      </div>
                      <div className="mt-0.5 font-mono text-[10px] text-blue-100/80">{r.condition || "—"}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-1 border-t border-white/10 bg-black/20 px-3 py-2 text-center">
                    <div>
                      <div className="font-mono text-[10px] text-red-300">↑{r.max_c != null ? toUnit(r.max_c) : "—"}{uSym}</div>
                      <div className="font-mono text-[9px] text-blue-100/60">Max</div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] text-cyan-300">↓{r.min_c != null ? toUnit(r.min_c) : "—"}{uSym}</div>
                      <div className="font-mono text-[9px] text-blue-100/60">Min</div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] text-blue-300">{r.humidity ?? "—"}%</div>
                      <div className="font-mono text-[9px] text-blue-100/60">Hum</div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] text-slate-200">{r.wind_kmh ?? "—"}</div>
                      <div className="font-mono text-[9px] text-blue-100/60">km/h</div>
                    </div>
                  </div>
                  {/* Weather Impact on Mobilisation — auto-updates with live weather */}
                  <div className={`border-t px-3 py-2 ${style.cls}`} data-testid={`weather-impact-${r.state}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        <ToneIcon className="h-3.5 w-3.5" />
                        <span className="industrial-label text-[9px]">Mobilisation Impact</span>
                      </div>
                      <span className={`rounded px-1.5 py-0.5 font-mono text-[9px] font-bold ${style.labelCls}`}>
                        {style.label}{impact.pct ? ` · ${impact.pct}%` : ""}
                      </span>
                    </div>
                    <div className="mt-1 text-[11px] font-bold leading-tight">{impact.title}</div>
                    <div className="mt-0.5 text-[10px] leading-tight opacity-90">{impact.detail}</div>
                  </div>
                </div>
              );
            })}
      </div>
      {generatedAt && (
        <div className="mt-2 text-right font-mono text-[10px] text-blue-100/50">
          Updated: {new Date(generatedAt).toLocaleString()}
        </div>
      )}
    </section>
  );
}
